import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../constants';

/**
 * Manages soft cart (tried on) and hard cart (want to buy) state,
 * syncing all changes to the backend.
 */
export function useCart(session) {
  const [softCart, setSoftCart] = useState([]);
  const [hardCart, setHardCart] = useState([]);

  const isInCart = useCallback(
    (productId) => hardCart.some((p) => (p.product_id || p.id) === productId),
    [hardCart]
  );

  const handleAddToCart = useCallback(
    async (product) => {
      if (!product || !session) return;
      const productId = product.product_id || product.id;
      if (hardCart.find((p) => (p.product_id || p.id) === productId)) {
        toast.error('Already in cart');
        return;
      }
      setHardCart((prev) => [...prev, product]);
      try {
        const response = await fetch(
          `${API_URL}/api/public-studio/session/hard-cart/add?session_token=${session.session_token}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ product_id: productId }),
          }
        );
        if (!response.ok) throw new Error('Failed to add to cart');
        toast.success('Added to cart!');
      } catch (err) {
        console.error('Failed to sync cart:', err);
        setHardCart((prev) => prev.filter((p) => (p.product_id || p.id) !== productId));
        toast.error('Failed to add to cart');
      }
    },
    [session, hardCart]
  );

  const handleRemoveFromCart = useCallback(
    async (product) => {
      if (!product || !session) return;
      const productId = product.product_id || product.id;
      setHardCart((prev) => prev.filter((p) => (p.product_id || p.id) !== productId));
      try {
        await fetch(
          `${API_URL}/api/public-studio/session/hard-cart/${productId}?session_token=${session.session_token}`,
          { method: 'DELETE' }
        );
        toast.success('Removed from cart');
      } catch (err) {
        console.error('Failed to remove from cart:', err);
        setHardCart((prev) => [...prev, product]);
        toast.error('Failed to remove from cart');
      }
    },
    [session, hardCart]
  );

  const handleCheckout = useCallback(() => {
    if (hardCart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    const total = hardCart.reduce((sum, p) => sum + (p.selling_price || p.price || 0), 0);
    const itemCount = hardCart.length;
    toast.success(
      `Please proceed to the checkout counter with your ${itemCount} item${itemCount > 1 ? 's' : ''} (₹${total.toLocaleString()})!`,
      { duration: 6000 }
    );
  }, [hardCart]);

  return {
    softCart,
    setSoftCart,
    hardCart,
    setHardCart,
    isInCart,
    handleAddToCart,
    handleRemoveFromCart,
    handleCheckout,
  };
}
